import { useState } from 'react';
import { Form, Row, Col, Button, Card, Image} from 'react-bootstrap';
import ProfileMobileSectionActions from './ProfileMobileSectionActions';
import icFactory from './assets/img/ic-factory.svg';
import icBriefcase from './assets/img/ic-briefcase.svg';
import ResponsiveMultiSelect from './components/Profile/ResponsiveMultiSelect';
import industriesData from './data/industries';

const formatIndustryOptionLabel = (option, { omitHierarchyIndent = false } = {}) => (
    <span
        className="industry-preferences-option-label"
        style={{
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
            paddingLeft: omitHierarchyIndent ? 0 : `${option.level * 10}px`,
            textAlign: 'left',
        }}
    >
        {option.label}
    </span>
);

const UserAddJob = ({ handleReset, formData, handleChange, errors, isEditPage, handleJobPreferencesSubmit, hideEdit, isOnboarding = false, isLastSection = false }) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [localErrors, setLocalErrors] = useState({});

    const industryOptions = [];

    industriesData.forEach(ind => {
        // Top-level
        industryOptions.push({
            value: ind.code,
            label: ind.name,
            level: 0
        });

        ind.subIndustries.forEach(sub => {
            // Sub-industry
            industryOptions.push({
                value: sub.code,
                label: sub.name,
                level: 1
            });

            sub.subSubIndustries.forEach(subsub => {
                // Sub-sub-industry
                industryOptions.push({
                    value: subsub.code,
                    label: subsub.name,
                    level: 2
                });
            });
        });
    });

    

    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();
        if (typeof handleJobPreferencesSubmit === 'function') { // ✅ Ensure it's a function before calling
            await handleJobPreferencesSubmit(e);
            setIsEditing(false); // ✅ Exit edit mode after saving
        } else {
            console.error('Error: handleJobPreferencesSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false); // Exit edit mode

        if (typeof handleReset === 'function') {
            handleReset(tempFormData); // ✅ Restore saved values + clear errors
        }
    };

    return (
        <>
        <Card className={isOnboarding && isLastSection ? 'mb-0 mb-md-4' : 'mb-4'}>
            <Card.Body>
                    {/* Title and Edit Button */}
                    <Row>
                        <Col>
                            <div className="profile-section-heading-row">
                            {isEditPage && !hideEdit && (
                                <div className="float-end d-none d-lg-block">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3 className="company-detail-section-title d-lg-none"><span className="item-number">2.</span> Job Preferences</h3>
                            <h3 className="d-none d-lg-flex"><span className="item-number">2.</span> Job Preferences</h3>
                            </div>
                            <hr />
                        </Col>
                    </Row>
                    <>
                        {isEditing ? (
                            <>
                                <Row>
                                    {/* Form Fields */}
                                    <Col md={12} className="profile-edit-fields">
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <Form.Group className="validated" controlId="industryJobPreferences">
                                                    <Form.Label>Industry Preferences</Form.Label>
                                                    <ResponsiveMultiSelect
                                                        label="Industry Preferences"
                                                        name="industryJobPreferences"
                                                        inputId="industryJobPreferences"
                                                        placeholder="Select industry preferences"
                                                        options={industryOptions}
                                                        value={(formData.industryJobPreferences || []).map(code => {
                                                            const match = industryOptions.find(opt => opt.value === code);
                                                            return match || { value: code, label: code }; // fallback
                                                        })}
                                                        onChange={(selectedOptions) => {
                                                            const selectedCodes = selectedOptions.map(option => option.value);
                                                            handleChange({
                                                            target: {
                                                                name: 'industryJobPreferences',
                                                                value: selectedCodes
                                                            }
                                                            });
                                                        }}
                                                        formatOptionLabel={formatIndustryOptionLabel}
                                                        mobileFormatOptionLabel={(option) => formatIndustryOptionLabel(option, { omitHierarchyIndent: true })}
                                                        className="react-select"
                                                        classNamePrefix="react-select"
                                                        isDisabled={!isEditing}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Label>Employment Type</Form.Label>
                                                    <Form.Select
                                                        name="employmentJobPreferences"
                                                        value={formData.employmentJobPreferences || ''}
                                                        onChange={handleChange}
                                                        disabled={!isEditing}
                                                    >
                                                        <option value="">Select employment type</option>
                                                        <option value="Permanent">Permanent</option>
                                                        <option value="Temporary">Temporary</option>
                                                    </Form.Select>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="validated" controlId="fteJobPreferences">
                                                    <Form.Label>FTE</Form.Label>
                                                    <Form.Select
                                                        name="fteJobPreferences"
                                                        value={formData.fteJobPreferences || ''}
                                                        onChange={handleChange}
                                                        disabled={!isEditing}
                                                    >
                                                        <option value="">Select FTE</option>
                                                        <option value="Full-time">Full-time</option>
                                                        <option value="Part-time">Part-time</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="validated" controlId="jobTypeJobPreferences">
                                                    <Form.Label>Job Type</Form.Label>
                                                    <Form.Select
                                                        name="jobTypeJobPreferences"
                                                        value={formData.jobTypeJobPreferences || ''}
                                                        onChange={handleChange}
                                                        disabled={!isEditing}
                                                    >
                                                        <option value="">Select job type</option>
                                                        <option value="Regular">Regular</option>
                                                        <option value="Side job">Side job</option>
                                                        <option value="Holiday job">Holiday job</option>
                                                        <option value="Intern">Intern</option>
                                                        <option value="Apprenticeship">Apprenticeship</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </>
                        ) : (
                            <>
                                <Row className="mb-4 job-preferences-industry-row">
                                    <Col md={12}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icFactory} alt="Industry" />
                                            </span>
                                            <span className="text">
                                                Industry
                                                <br />
                                                <strong>
                                                    {(
                                                        (formData.industryJobPreferences ?? [])
                                                            .map(code => industryOptions.find(opt => opt.value === code)?.label ?? code) // ✅ label or code fallback
                                                            .join(', ')
                                                        ) || 'N/A'
                                                    }
                                                    </strong>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="mb-4">
                                    <Col md={4}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icBriefcase} alt="Employment type" />
                                            </span>
                                            <span className="text">
                                                Employment type
                                                <br />
                                                <strong>    
                                                    {formData.employmentJobPreferences || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icBriefcase} alt="FTE" />
                                            </span>
                                            <span className="text">
                                                FTE
                                                <br />
                                                <strong>    
                                                    {formData.fteJobPreferences || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icBriefcase} alt="Job type" />
                                            </span>
                                            <span className="text">
                                                Job type
                                                <br />
                                                <strong>    
                                                    {formData.jobTypeJobPreferences || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </>

                    <ProfileMobileSectionActions
                        isEditing={isEditing}
                        onEdit={handleEditClick}
                        onCancel={handleCancelClick}
                        onSave={handleSaveClick}
                        show={isEditPage && !hideEdit}
                    />
                </Card.Body>
            </Card>
        </>
    );
};

export default UserAddJob
